import { useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import type {
    ColumnSelectionOptions,
    TableColumnType,
} from "app/pages/sales-intelligence/common-components/column-picker";
import { setColumnNamesForHighlight } from "app/pages/sales-intelligence/modules/leads-search";
import type { LeadsSearchColumnListConfig } from "app/pages/sales-intelligence/modules/leads-search/types";
import { mergeColumnOptionsWithMissingIndustryCodeColumns } from "app/pages/sales-intelligence/modules/leads-search/utils/columns";
import { selectDirtyFilters } from "app/pages/sales-intelligence/modules/leads-search-filters";
import type ColumnSuggestion from "app/pages/sales-intelligence/sub-modules/column-suggestions/models/ColumnSuggestion";

import { useCreateColumnSuggestions } from "./useCreateColumnSuggestions";

export type SuggestionsApplyFunc = (columns: readonly string[], appliedColumnNames: readonly string[]) => Promise<void>;

type ApplyFunc = (appliedColumnNames: readonly string[]) => Promise<void>;

const useFilterColumnSuggestions = (
    onSuggestionsApply: SuggestionsApplyFunc,
    selected: readonly string[],
    available: ColumnSelectionOptions,
    columnListConfig: LeadsSearchColumnListConfig,
): [readonly ColumnSuggestion[], ApplyFunc] => {
    const dispatch = useDispatch();

    const dirtyFilters = useSelector(selectDirtyFilters);

    const columnsForSuggestions = useMemo(
        () => mergeColumnOptionsWithMissingIndustryCodeColumns(available, columnListConfig),
        [available, columnListConfig],
    );

    const selectedColumnNames: readonly TableColumnType["id"][] = selected;

    const createColumnSuggestions = useCreateColumnSuggestions(columnsForSuggestions);

    const filterColumnSuggestions = useMemo(() => {
        if (!createColumnSuggestions) {
            return [];
        }

        try {
            return createColumnSuggestions(dirtyFilters, selectedColumnNames);
        } catch (error) {
            Sentry.errorTracker({ message: "Error creating column suggestions", e: error, level: "error" });

            return [];
        }
    }, [createColumnSuggestions, dirtyFilters, selectedColumnNames]);

    const handleSuggestionsApply = async (appliedColumnNames: readonly string[]) => {
        const columns = [...selectedColumnNames, ...appliedColumnNames];

        await onSuggestionsApply(columns, appliedColumnNames);

        dispatch(setColumnNamesForHighlight(appliedColumnNames));
    };

    return [filterColumnSuggestions, handleSuggestionsApply];
};

export default useFilterColumnSuggestions;
